// Three.js neon button engine from neon-start-button (1).html
// Ribbons + ring + halos + bloom — no particles, no text
// Supports active/inactive state and audio reactivity

import * as THREE from 'three';

// ── noise ──────────────────────────────────────────────────────────
function fbm(x: number, t: number) {
  return (
    Math.sin(x * 1.7 + t * 0.31) * 0.5 +
    Math.sin(x * 3.4 + t * 0.17 + 1.2) * 0.28 +
    Math.sin(x * 7.1 + t * 0.09 + 2.5) * 0.14 +
    Math.sin(x * 13.3 + t * 0.05 + 4.1) * 0.07
  );
}

// ── shaders ────────────────────────────────────────────────────────
const quadVert = `varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.0,1.0);}`;
const objVert = `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`;

const ringVert = `
  varying float vAngle;
  void main(){vAngle=atan(position.y,position.x);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`;

const ringFrag = `
  varying float vAngle;uniform float time;uniform float fade;uniform float audio;
  #define PI 3.14159265
  void main(){
    float t=fract((vAngle+PI)/(2.0*PI)+time*0.04);
    vec3 c=t<0.5
      ?mix(vec3(0.0,0.95,1.0),vec3(1.0,0.0,0.85),t*2.0)
      :mix(vec3(1.0,0.0,0.85),vec3(0.52,0.0,1.0),(t-0.5)*2.0);
    float bright=(0.82+0.18*sin(t*12.0+time*1.4))*(0.15+0.85*fade)*(1.0+audio*0.5);
    gl_FragColor=vec4(c*bright,1.0);
  }`;

const btnFrag = `
  varying vec2 vUv;uniform float time;uniform float fade;
  void main(){
    vec2 uv=vUv*2.0-1.0;float r=length(uv);
    float g=(1.0-smoothstep(0.0,1.0,r))*0.07*(0.8+0.2*sin(time*0.7));
    float dim=0.4+0.6*fade;
    gl_FragColor=vec4((mix(vec3(0.0,0.22,0.35),vec3(0.0,0.0,0.03),r)+vec3(0.0,g,g*1.3))*dim,1.0);
  }`;

const ribbonFrag = `
  uniform vec3 colorA,colorB;uniform float opacity;varying vec2 vUv;
  void main(){
    float e=abs(vUv.x-0.5)*2.0;
    float a=(exp(-e*e*9.0)+exp(-e*e*2.2)*0.28)*opacity
           *smoothstep(0.0,0.05,vUv.y)*smoothstep(1.0,0.95,vUv.y);
    gl_FragColor=vec4(mix(colorA,colorB,vUv.y+(vUv.x-0.5)*0.3),a);
  }`;

const blurFragH = `uniform sampler2D tDiffuse;uniform vec2 res;varying vec2 vUv;
  void main(){vec4 c=vec4(0.0);float w[11];w[0]=.006;w[1]=.020;w[2]=.052;w[3]=.100;w[4]=.149;w[5]=.175;w[6]=.149;w[7]=.100;w[8]=.052;w[9]=.020;w[10]=.006;
  for(int i=0;i<11;i++)c+=texture2D(tDiffuse,vUv+vec2(float(i-5)/res.x,0.0))*w[i];gl_FragColor=c;}`;

const blurFragV = `uniform sampler2D tDiffuse;uniform vec2 res;varying vec2 vUv;
  void main(){vec4 c=vec4(0.0);float w[11];w[0]=.006;w[1]=.020;w[2]=.052;w[3]=.100;w[4]=.149;w[5]=.175;w[6]=.149;w[7]=.100;w[8]=.052;w[9]=.020;w[10]=.006;
  for(int i=0;i<11;i++)c+=texture2D(tDiffuse,vUv+vec2(0.0,float(i-5)/res.y))*w[i];gl_FragColor=c;}`;

const compositeFrag = `
  uniform sampler2D tBase,tBloom1,tBloom2,tBloom3;uniform float hovered,burst;varying vec2 vUv;
  void main(){
    float boost=1.0+hovered*0.35+burst*0.4;
    vec2 dir=vUv-0.5;float sp=0.0018+burst*0.004;
    vec4 b1=vec4(texture2D(tBloom1,vUv+dir*sp).r,texture2D(tBloom1,vUv).g,texture2D(tBloom1,vUv-dir*sp).b,texture2D(tBloom1,vUv).a);
    vec4 base=texture2D(tBase,vUv);
    vec3 bloom=b1.rgb*2.0*boost+texture2D(tBloom2,vUv).rgb*1.4*boost+texture2D(tBloom3,vUv).rgb*0.8;
    vec3 color=base.rgb+bloom;
    color.r*=0.92;color.b*=1.04;
    float bright=max(color.r,max(color.g,color.b));
    float a=max(base.a,smoothstep(0.0,0.08,bright));
    gl_FragColor=vec4(color,a);
  }`;

const rippleFrag = `uniform float progress,opacity;varying vec2 vUv;
  void main(){vec2 uv=vUv*2.0-1.0;float r=length(uv);
  float a=exp(-((r-progress)/0.04)*((r-progress)/0.04))*opacity*(1.0-progress);
  gl_FragColor=vec4(mix(vec3(0.0,0.9,1.0),vec3(0.8,0.0,1.0),progress),a);}`;

// ── ribbon geometry ────────────────────────────────────────────────
const N_SEG = 240;

interface RibbonCfg {
  phaseOffset: number;
  baseRadius: number;
  amp: number;
  freqA: number;
  freqB: number;
  rotSpeed: number;
  rotDir: number;
  width: number;
}

function allocRibbon() {
  const v = (N_SEG + 1) * 2;
  const pos = new Float32Array(v * 3);
  const uv = new Float32Array(v * 2);
  const idx = new Uint16Array(N_SEG * 6);
  for (let j = 0; j <= N_SEG; j++) {
    const t = j / N_SEG;
    uv[j * 4] = 0;
    uv[j * 4 + 1] = t;
    uv[j * 4 + 2] = 1;
    uv[j * 4 + 3] = t;
  }
  for (let j = 0; j < N_SEG; j++) {
    const b = j * 6,
      q = j * 2;
    idx[b] = q;
    idx[b + 1] = q + 1;
    idx[b + 2] = q + 3;
    idx[b + 3] = q;
    idx[b + 4] = q + 3;
    idx[b + 5] = q + 2;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  return g;
}

function updateRibbon(
  geo: THREE.BufferGeometry,
  t: number,
  cfg: RibbonCfg,
  i: number,
  audioAmp: number
) {
  const p = (geo.attributes.position as THREE.BufferAttribute).array as Float32Array;
  const {
    phaseOffset: ph,
    baseRadius: br,
    amp,
    freqA,
    freqB,
    rotSpeed: rs,
    rotDir: rd,
    width: wd,
  } = cfg;
  const ampM = amp * (1 + audioAmp * 1.5);
  const a0 = ph + t * rs * rd;
  const cx = new Float32Array(N_SEG + 1);
  const cy = new Float32Array(N_SEG + 1);
  for (let j = 0; j <= N_SEG; j++) {
    const s = j / N_SEG;
    const a = s * Math.PI * 2 + a0;
    const r =
      br +
      fbm(s * freqA + t * 0.22, t) * ampM +
      Math.sin(s * freqB * Math.PI * 2 + t * 0.38 + ph) * ampM * 0.35;
    cx[j] = Math.cos(a) * r;
    cy[j] = Math.sin(a) * r;
  }
  for (let j = 0; j <= N_SEG; j++) {
    const jn = Math.min(j + 1, N_SEG),
      jp = Math.max(j - 1, 0);
    const tx = cx[jn] - cx[jp],
      ty = cy[jn] - cy[jp];
    const l = Math.sqrt(tx * tx + ty * ty) || 1;
    const px = -ty / l,
      py = tx / l;
    const s = j / N_SEG;
    const w =
      wd *
      (Math.sin(s * Math.PI * 5 + t * 0.7 + i) * 0.25 + 1.0) *
      (0.5 + Math.sin(s * Math.PI) * 0.5);
    const b = j * 2;
    p[b * 3] = cx[j] + px * w;
    p[b * 3 + 1] = cy[j] + py * w;
    p[b * 3 + 2] = 0;
    p[(b + 1) * 3] = cx[j] - px * w;
    p[(b + 1) * 3 + 1] = cy[j] - py * w;
    p[(b + 1) * 3 + 2] = 0;
  }
  (geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
  geo.computeBoundingSphere();
}

// ── ribbon definitions ─────────────────────────────────────────────
const RDEFS = [
  {
    ph: 0.0,
    br: 0.659,
    amp: 0.123,
    fA: 2.1,
    fB: 2,
    rs: 0.07,
    rd: 1,
    wd: 0.054,
    cA: 0x0066ff,
    cB: 0x00ccff,
    op: 0.6,
  },
  {
    ph: 1.2,
    br: 0.697,
    amp: 0.115,
    fA: 1.8,
    fB: 3,
    rs: 0.055,
    rd: -1,
    wd: 0.046,
    cA: 0x0044dd,
    cB: 0x0099ff,
    op: 0.5,
  },
  {
    ph: 2.5,
    br: 0.644,
    amp: 0.138,
    fA: 2.4,
    fB: 2,
    rs: 0.065,
    rd: 1,
    wd: 0.061,
    cA: 0x0077ff,
    cB: 0x44ddff,
    op: 0.55,
  },
  {
    ph: 3.9,
    br: 0.72,
    amp: 0.107,
    fA: 1.6,
    fB: 4,
    rs: 0.048,
    rd: -1,
    wd: 0.038,
    cA: 0x0055cc,
    cB: 0x0088ee,
    op: 0.45,
  },
  {
    ph: 5.1,
    br: 0.621,
    amp: 0.084,
    fA: 3.2,
    fB: 3,
    rs: 0.09,
    rd: 1,
    wd: 0.034,
    cA: 0x00aaff,
    cB: 0x55ddff,
    op: 0.52,
  },
  {
    ph: 0.7,
    br: 0.601,
    amp: 0.1,
    fA: 2.9,
    fB: 3,
    rs: 0.1,
    rd: 1,
    wd: 0.046,
    cA: 0x8800ff,
    cB: 0xcc44ff,
    op: 0.65,
  },
  {
    ph: 1.9,
    br: 0.632,
    amp: 0.092,
    fA: 2.5,
    fB: 2,
    rs: 0.085,
    rd: -1,
    wd: 0.05,
    cA: 0xaa00ee,
    cB: 0xff44ff,
    op: 0.58,
  },
  {
    ph: 3.3,
    br: 0.594,
    amp: 0.107,
    fA: 3.3,
    fB: 3,
    rs: 0.11,
    rd: 1,
    wd: 0.038,
    cA: 0x6600bb,
    cB: 0xbb00ff,
    op: 0.6,
  },
  {
    ph: 5.0,
    br: 0.651,
    amp: 0.084,
    fA: 2.2,
    fB: 4,
    rs: 0.095,
    rd: -1,
    wd: 0.042,
    cA: 0x9900dd,
    cB: 0xdd66ff,
    op: 0.52,
  },
  {
    ph: 0.4,
    br: 0.575,
    amp: 0.069,
    fA: 4.0,
    fB: 3,
    rs: 0.14,
    rd: 1,
    wd: 0.027,
    cA: 0xff0088,
    cB: 0xff55cc,
    op: 0.78,
  },
  {
    ph: 1.6,
    br: 0.586,
    amp: 0.061,
    fA: 3.8,
    fB: 4,
    rs: 0.13,
    rd: -1,
    wd: 0.023,
    cA: 0xff0077,
    cB: 0xff44ee,
    op: 0.72,
  },
  {
    ph: 3.0,
    br: 0.567,
    amp: 0.077,
    fA: 4.2,
    fB: 3,
    rs: 0.155,
    rd: 1,
    wd: 0.031,
    cA: 0xdd0066,
    cB: 0xff33bb,
    op: 0.68,
  },
  {
    ph: 4.8,
    br: 0.582,
    amp: 0.054,
    fA: 3.6,
    fB: 5,
    rs: 0.145,
    rd: -1,
    wd: 0.023,
    cA: 0xff0066,
    cB: 0xff77cc,
    op: 0.74,
  },
  {
    ph: 0.9,
    br: 0.751,
    amp: 0.161,
    fA: 1.4,
    fB: 2,
    rs: 0.035,
    rd: 1,
    wd: 0.138,
    cA: 0x001e66,
    cB: 0x003388,
    op: 0.22,
  },
  {
    ph: 3.6,
    br: 0.781,
    amp: 0.153,
    fA: 1.2,
    fB: 2,
    rs: 0.028,
    rd: -1,
    wd: 0.153,
    cA: 0x3d0066,
    cB: 0x5500aa,
    op: 0.18,
  },
  {
    ph: 2.1,
    br: 0.736,
    amp: 0.169,
    fA: 1.5,
    fB: 2,
    rs: 0.04,
    rd: 1,
    wd: 0.123,
    cA: 0x002266,
    cB: 0x004488,
    op: 0.16,
  },
];

// ── main setup ─────────────────────────────────────────────────────
export function setupNeonScene(canvas: HTMLCanvasElement, canvasSize: number) {
  const CW = canvasSize;
  const CH = canvasSize;
  const DPR = Math.min(devicePixelRatio, 2);
  const RADIUS = 0.544;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    premultipliedAlpha: false,
  });
  renderer.setPixelRatio(DPR);
  renderer.setSize(CW, CH);
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(52, 1, 0.1, 100);
  camera.position.z = 5;

  // render targets (DPR-scaled for sharp rendering on high-density screens)
  const mk = (w: number, h: number) =>
    new THREE.WebGLRenderTarget(w, h, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
  const PW = CW * DPR,
    PH = CH * DPR;
  const baseRT = mk(PW, PH);
  const b1A = mk(PW / 2, PH / 2),
    b1B = mk(PW / 2, PH / 2);
  const b2A = mk(PW / 4, PH / 4),
    b2B = mk(PW / 4, PH / 4);
  const b3A = mk(PW / 8, PH / 8),
    b3B = mk(PW / 8, PH / 8);

  // quad for post-processing
  const qGeo = new THREE.PlaneGeometry(2, 2);
  const qCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const qScene = new THREE.Scene();
  const qMesh = new THREE.Mesh(qGeo);
  qScene.add(qMesh);

  const mkB = (w: number, h: number, f: string) =>
    new THREE.ShaderMaterial({
      uniforms: { tDiffuse: { value: null }, res: { value: new THREE.Vector2(w, h) } },
      vertexShader: quadVert,
      fragmentShader: f,
    });
  const mBH1 = mkB(PW / 2, PH / 2, blurFragH),
    mBV1 = mkB(PW / 2, PH / 2, blurFragV);
  const mBH2 = mkB(PW / 4, PH / 4, blurFragH),
    mBV2 = mkB(PW / 4, PH / 4, blurFragV);
  const mBH3 = mkB(PW / 8, PH / 8, blurFragH),
    mBV3 = mkB(PW / 8, PH / 8, blurFragV);

  const matComp = new THREE.ShaderMaterial({
    uniforms: {
      tBase: { value: null },
      tBloom1: { value: null },
      tBloom2: { value: null },
      tBloom3: { value: null },
      hovered: { value: 0 },
      burst: { value: 0 },
    },
    vertexShader: quadVert,
    fragmentShader: compositeFrag,
    transparent: true,
  });

  // ── scene objects ────────────────────────────────────────────────

  // button face
  const btnMat = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, fade: { value: 0 } },
    vertexShader: objVert,
    fragmentShader: btnFrag,
  });
  scene.add(new THREE.Mesh(new THREE.CircleGeometry(RADIUS - 0.012, 128), btnMat));

  // neon ring
  const ringMat = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, fade: { value: 0 }, audio: { value: 0 } },
    vertexShader: ringVert,
    fragmentShader: ringFrag,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  scene.add(new THREE.Mesh(new THREE.RingGeometry(RADIUS - 0.01, RADIUS + 0.01, 512), ringMat));

  // halos
  const haloMats: THREE.MeshBasicMaterial[] = [];
  const addHalo = (r: number, t: number, c: number, baseOp: number) => {
    const m = new THREE.MeshBasicMaterial({
      color: c,
      transparent: true,
      opacity: baseOp,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    haloMats.push(m);
    scene.add(new THREE.Mesh(new THREE.RingGeometry(r, r + t, 256), m));
    return baseOp;
  };
  const haloBaseOps = [
    addHalo(RADIUS - 0.027, 0.077, 0x4400cc, 0.12),
    addHalo(RADIUS - 0.015, 0.038, 0x8800ff, 0.2),
    addHalo(RADIUS + 0.011, 0.031, 0x00ccff, 0.18),
    addHalo(RADIUS + 0.004, 0.084, 0x006699, 0.09),
  ];

  const bgMat = new THREE.MeshBasicMaterial({
    color: 0x110022,
    transparent: true,
    opacity: 0.18,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  scene.add(new THREE.Mesh(new THREE.CircleGeometry(RADIUS * 2.4, 128), bgMat));

  // ribbons
  const ribbons = RDEFS.map(({ ph, br, amp, fA, fB, rs, rd, wd, cA, cB, op }) => {
    const cfg: RibbonCfg = {
      phaseOffset: ph,
      baseRadius: br,
      amp,
      freqA: fA,
      freqB: fB,
      rotSpeed: rs,
      rotDir: rd,
      width: wd,
    };
    const geo = allocRibbon();
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        colorA: { value: new THREE.Color(cA) },
        colorB: { value: new THREE.Color(cB) },
        opacity: { value: op },
      },
      vertexShader: objVert,
      fragmentShader: ribbonFrag,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    scene.add(new THREE.Mesh(geo, mat));
    return { geo, cfg, mat, op };
  });

  // ripples
  const mkRip = (sz: number) => {
    const m = new THREE.ShaderMaterial({
      uniforms: { progress: { value: 0 }, opacity: { value: 0 } },
      vertexShader: objVert,
      fragmentShader: rippleFrag,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    scene.add(new THREE.Mesh(new THREE.PlaneGeometry(sz, sz), m));
    return m;
  };
  const rip1 = mkRip(RADIUS * 2.8);
  const rip2 = mkRip(RADIUS * 4.0);
  let rT1 = 0,
    rT2 = -0.25,
    rActive = false;

  // ── state ────────────────────────────────────────────────────────
  let hoveredState = false;
  let burstVal = 0;
  let scaleCur = 1;
  let isActive = false;
  let fadeAlpha = 0;
  let micI = 0;
  let srvI = 0;

  const clock = new THREE.Clock();

  const bp = (
    src: THREE.Texture,
    dH: THREE.WebGLRenderTarget,
    dV: THREE.WebGLRenderTarget,
    mH: THREE.ShaderMaterial,
    mV: THREE.ShaderMaterial
  ) => {
    mH.uniforms.tDiffuse.value = src;
    qMesh.material = mH;
    renderer.setRenderTarget(dH);
    renderer.render(qScene, qCam);
    mV.uniforms.tDiffuse.value = dH.texture;
    qMesh.material = mV;
    renderer.setRenderTarget(dV);
    renderer.render(qScene, qCam);
  };

  let animId: number;

  function animate() {
    animId = requestAnimationFrame(animate);
    const t = clock.getElapsedTime();

    // fade transition
    fadeAlpha += ((isActive ? 1 : 0) - fadeAlpha) * 0.06;

    btnMat.uniforms.time.value = t;
    btnMat.uniforms.fade.value = fadeAlpha;
    ringMat.uniforms.time.value = t;
    ringMat.uniforms.fade.value = fadeAlpha;
    ringMat.uniforms.audio.value = micI;

    burstVal *= 0.9;
    scaleCur += ((hoveredState ? 1.045 : 1.0) - scaleCur) * 0.08;
    scene.scale.setScalar(scaleCur);

    // halos fade
    haloMats.forEach((m, i) => {
      m.opacity = haloBaseOps[i] * (0.05 + 0.95 * fadeAlpha);
    });
    bgMat.opacity = 0.18 * (0.1 + 0.9 * fadeAlpha);

    // ribbons
    ribbons.forEach(({ geo, cfg, mat, op }, i) => {
      updateRibbon(geo, t, cfg, i, micI * fadeAlpha);
      mat.uniforms.opacity.value =
        (op * (0.78 + 0.22 * Math.sin(t * 1.2 + i * 0.7)) +
          (hoveredState ? 0.08 : 0) +
          burstVal * 0.22 +
          micI * 0.15) *
        fadeAlpha;
    });

    // ripples
    if (rActive) {
      rT1 += 0.016;
      rT2 += 0.016;
      rip1.uniforms.progress.value = Math.min(rT1, 1.0);
      rip1.uniforms.opacity.value = Math.max(0, 1.0 - rT1 * 0.9);
      rip2.uniforms.progress.value = Math.max(0, Math.min(rT2, 1.0));
      rip2.uniforms.opacity.value = Math.max(0, 1.0 - Math.max(0, rT2) * 0.85);
      if (rT1 > 1.2) rActive = false;
    }

    // audio-reactive scale pulse
    const audioPulse = srvI * 0.02 * fadeAlpha;
    scene.scale.setScalar(scaleCur + audioPulse);

    // render
    renderer.setRenderTarget(baseRT);
    renderer.render(scene, camera);
    bp(baseRT.texture, b1A, b1B, mBH1, mBV1);
    bp(b1B.texture, b2A, b2B, mBH2, mBV2);
    bp(b2B.texture, b3A, b3B, mBH3, mBV3);

    matComp.uniforms.tBase.value = baseRT.texture;
    matComp.uniforms.tBloom1.value = b1B.texture;
    matComp.uniforms.tBloom2.value = b2B.texture;
    matComp.uniforms.tBloom3.value = b3B.texture;
    matComp.uniforms.hovered.value +=
      ((hoveredState ? 1 : 0) - matComp.uniforms.hovered.value) * 0.09;
    matComp.uniforms.burst.value = burstVal;

    qMesh.material = matComp;
    renderer.setRenderTarget(null);
    renderer.render(qScene, qCam);
  }

  animate();

  return {
    setActive(v: boolean) {
      isActive = v;
    },
    setHovered(v: boolean) {
      hoveredState = v;
    },
    setAudio(mic: number, srv: number) {
      micI = mic;
      srvI = srv;
    },
    triggerBurst() {
      burstVal = 1.0;
      rT1 = 0;
      rT2 = -0.22;
      rActive = true;
    },
    dispose() {
      cancelAnimationFrame(animId);
      renderer.dispose();
      [baseRT, b1A, b1B, b2A, b2B, b3A, b3B].forEach((rt) => rt.dispose());
    },
  };
}
